import {
    BabyLoungeIcon,
    CustomerServiceCenterIcon,
    ElevatorIcon,
    EscalatorIcon,
    ExchangeIcon,
    GiftRedemptionIcon,
    InformationIcon,
    RestRoomIcon,
    StorageIcon,
    StrollerRentalIcon,
} from '@/components/icons/floor-guide/categories';
import { TaxRefundIcon } from '@/components/icons/floor-guide/categories/TaxRefund';
import { CATEGORY_CODE_ID, CATEGORY_ORDER, MAPPING_APP_LANGUAGE_TO_MAP_LANGUAGE, MAPPING_MENU_LANGUAGE, OBJECT_STYLE_SET } from '@/constants/floor-guide';
import { RegexConstants } from '@/constants/regex';
import { CategoryType } from '@/enums/floor-guide';
import type { BuildingCategory, PoiContent } from '@/services/dto';
import type { BuildingInStoreInfo, KioskSelectedCategory } from '@/services/dto/kiosk-config';
import type { languageType } from '@/stores';
import type { GroupedPoiByBuildingId, GroupedPoiByFloorId, PoiItem } from '@/types/map';
import { getGroupedPoiForBuilding } from '@/utils/map';
import { LANG_TYPE, type DabeeoMap, type DabeeoMapData } from 'dabeeomaps';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import type { TFunction } from 'i18next';

const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';
const CONTRAST_BACKGROUND_COLOR = '#FFFFFF';

export const sortCategories = (categories?: BuildingCategory[]) => {
    if (!Array.isArray(categories) || !categories?.length) {
        return [];
    }

    return [...categories].sort((a, b) => {
        const orderA = CATEGORY_ORDER[a.clientCode] ?? Infinity;
        const orderB = CATEGORY_ORDER[b.clientCode] ?? Infinity;
        return orderA - orderB;
    });
};

export const updateObjectGroup = (mapData: DabeeoMapData | undefined, objectGroup: { [key: string]: any[] }) => {
    if (!mapData) {
        return;
    }
    const objects = mapData.dataObject.getObjects();
    if (!objects) {
        return;
    }
    const groupKeys = Object.keys(objectGroup);
    groupKeys.forEach((key) => {
        objectGroup[key] = [];
    });
    // 분류
    for (const obj of objects) {
        const key = obj.objectType;
        if (key && groupKeys.includes(key)) {
            objectGroup[key].push(obj);
        }
    }
};

export const updateObjectStyle = (currentMap: DabeeoMap | undefined, objectGroup: { [key: string]: any[] }) => {
    const groupKeys = Object.keys(objectGroup);
    groupKeys.forEach((key) => {
        const typedKey = key as keyof typeof OBJECT_STYLE_SET;
        const colorSet = OBJECT_STYLE_SET[typedKey];
        const ids = objectGroup[key].map((obj) => obj.id);
        currentMap?.objects?.set({
            ids,
            color: colorSet.color,
            strokeColor: colorSet.stroke,
            strokeWidth: 2,
            strokeOpacity: 1,
            opacity: 1,
        });
    });
    currentMap?.context?.setCanvasColor(CONTRAST_BACKGROUND_COLOR);
};

export const resetObjectStyle = (currentMap: DabeeoMap | undefined) => {
    currentMap?.objects.reset();
    currentMap?.context.setCanvasColor(DEFAULT_BACKGROUND_COLOR);
};

export const getFloorDisplayLabel = (name: string): string => {
    const trimmedName = name.trim();
    const spaceIndex = trimmedName.indexOf(' ');
    if (spaceIndex === -1) {
        return trimmedName;
    }
    return trimmedName.slice(spaceIndex + 1).trim();
};

export const getFloorButtonLabel = (name: string): string => {
    const label = getFloorDisplayLabel(name);
    if (label.toUpperCase() === 'G') {
        return 'G(ground)';
    }
    return label;
};

export const generateFloorName = (rawFloorName: string) => {
    if (!rawFloorName) return '';
    const lowerCaseFloorName = rawFloorName.toLowerCase();
    const hasFCharacter = RegexConstants.FLOOR_NUMBER_WITH_F_REGEX.test(lowerCaseFloorName);
    if (hasFCharacter) {
        const floorNumber = lowerCaseFloorName.replace(RegexConstants.FLOOR_NUMBER_WITH_F_REGEX, (_, digits) => digits).trim();
        return floorNumber;
    }
    return rawFloorName;
};

export const generateFloorNameWithTTS = (t: TFunction, floorName: string, selected: boolean = false) => {
    const status = selected ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected');

    return `${status} ${floorName} ${t('common.button.tts.tab_button')}`;
};

export const generateAllFloorsGuideWithTTS = (t: TFunction, buttonText: string, selected: boolean = false) => {
    const status = selected ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected');

    return `${status} ${buttonText} ${t('common.button.tts.switch_button')}`;
};

export const generateMapLanguage = (appLanguage: languageType) => {
    return MAPPING_APP_LANGUAGE_TO_MAP_LANGUAGE[appLanguage] ?? LANG_TYPE.DEFAULT;
};

export const generateMenuKey = (appLanguage: languageType) => {
    const key = MAPPING_MENU_LANGUAGE[appLanguage] ?? 'shopPrdcNm';
    return key;
};

export const generateGnbSvgColor = (isGnbActive: boolean, isContrast: boolean) => {
    if (isGnbActive) {
        return '--text-primary';
    }

    if (!isContrast) {
        return '--color-not-contrast-gray-444';
    }

    return '--color-not-contrast-white';
};

export const isNearbyPoiAvailable = (poiContentList?: PoiContent[], studioPoiId?: string) => {
    if (!Array.isArray(poiContentList) || !poiContentList?.length || !studioPoiId) {
        return false;
    }

    return poiContentList.some((poi) => poi.studioPoiId === studioPoiId);
};

export const getNearestAvailablePoiItem = (poiList?: PoiItem[], poiContentList?: PoiContent[]) => {
    if (!Array.isArray(poiList) || poiList.length === 0 || !Array.isArray(poiContentList) || poiContentList.length === 0) {
        return undefined;
    }

    if (poiList.length === 1) {
        const first = poiList[0];
        return isNearbyPoiAvailable(poiContentList, first.id) ? first : undefined;
    }

    let result: PoiItem | undefined = undefined;

    for (let i = 1; i < poiList.length; i++) {
        const poi = poiList[i];
        if (isNearbyPoiAvailable(poiContentList, poi.id)) {
            result = poi;
            break;
        }
    }

    if (!result) {
        const first = poiList[0];
        if (isNearbyPoiAvailable(poiContentList, first.id)) {
            result = first;
        }
    }

    return result;
};

export const sortPoiByTitle = (poiList?: IPoi[], locale: string = 'en') => {
    if (!Array.isArray(poiList) || poiList.length === 0) {
        return [];
    }

    return [...poiList].sort((a, b) =>
        (a?.title ?? '').localeCompare(b?.title ?? '', locale, {
            sensitivity: 'base',
        })
    );
};

export const getPoiItemByTitle = (filteredPoi?: IPoi | IPoi[], title?: string) => {
    if (filteredPoi === null || filteredPoi === undefined) {
        return undefined;
    }

    if (!Array.isArray(filteredPoi)) {
        return filteredPoi;
    }
    const sortedPoiList = sortPoiByTitle(filteredPoi);

    if (sortedPoiList?.length === 0) {
        return undefined;
    }

    const findPoi = sortedPoiList.find((poi) => poi?.title === title);

    if (findPoi) {
        return findPoi;
    }

    return sortedPoiList[0];
};

export const filterCategoriesWithMapPoi = (
    categories: KioskSelectedCategory[] | undefined,
    groupedPoiByBuildingId: GroupedPoiByBuildingId,
    buildingId?: string,
    storeBuildings?: BuildingInStoreInfo[]
): KioskSelectedCategory[] => {
    if (!categories?.length) {
        return [];
    }

    if (!groupedPoiByBuildingId.size) {
        return categories;
    }

    const buildingFloorMap = buildingId
        ? getGroupedPoiForBuilding(groupedPoiByBuildingId, buildingId, storeBuildings)
        : null;

    const floorMapsToCheck = buildingFloorMap
        ? Array.from(buildingFloorMap.values())
        : Array.from(groupedPoiByBuildingId.values()).flatMap((buildingMap) => Array.from(buildingMap.values()));

    if (buildingId && floorMapsToCheck.length === 0) {
        return [];
    }

    return categories.filter((category) => {
        const categoryCodes = [category.categoryCode, ...(category.mergeCategoryCode ?? [])].filter(Boolean);

        for (const floorMap of floorMapsToCheck) {
            for (const categoryCode of categoryCodes) {
                if (floorMap.get(categoryCode)?.length) {
                    return true;
                }
            }
        }

        return false;
    });
};

export const mergeSelectedCategoriesByClientCode = (categories: KioskSelectedCategory[]): KioskSelectedCategory[] => {
    if (!categories?.length) {
        return [];
    }

    const mergedMap = new Map<string, KioskSelectedCategory>();

    for (const category of categories) {
        if (!category.clientCode || category.clientCode === 'all') {
            continue;
        }

        const codesToAdd = category.mergeCategoryCode?.length
            ? category.mergeCategoryCode.filter(Boolean)
            : category.categoryCode
              ? [category.categoryCode]
              : [];

        if (!codesToAdd.length) {
            continue;
        }

        const existing = mergedMap.get(category.clientCode);
        if (!existing) {
            mergedMap.set(category.clientCode, {
                ...category,
                mergeCategoryCode: [...codesToAdd],
            });
            continue;
        }

        const mergedCodes = existing.mergeCategoryCode ?? [];
        for (const code of codesToAdd) {
            if (!mergedCodes.includes(code)) {
                mergedCodes.push(code);
            }
        }
        existing.mergeCategoryCode = mergedCodes;
    }

    return Array.from(mergedMap.values());
};

export const getMergedCategoryCodes = (category: KioskSelectedCategory): string[] => {
    return category.mergeCategoryCode?.length ? category.mergeCategoryCode : [category.categoryCode];
};

export const getPoisByFloorAndCategory = (
    groupedPoiByFloorId: GroupedPoiByFloorId,
    floorName: string,
    category: KioskSelectedCategory
): PoiItem[] => {
    const floorMap = groupedPoiByFloorId.get(floorName);
    if (!floorMap) {
        return [];
    }
    return getMergedCategoryCodes(category).flatMap((categoryCode) => floorMap.get(categoryCode) ?? []);
};

export const isNonEmptyObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length > 0;
};
